import React, { useCallback, useEffect, useState } from "react";
import api from "services/api";
import { __ } from "services/Translator";
import AuthGate from "components/auth/AuthGate";
import StoreContent, { CloverCrest } from "components/store/StoreContent";
import StoreTeaser from "components/store/StoreTeaser";
import {
  getStoreMockState,
  normalizeStoreData,
} from "components/store/storeMockData";

// Handoff/demo fixtures are an explicit opt-in only (?preview=1). They must
// NEVER surface for a real logged-in player when the API is unavailable —
// fake balances/rewards on an error would tell players they redeemed things
// that never touched the backend.
const PREVIEW_ENABLED =
  typeof window !== "undefined" &&
  /[?&]preview=1(?:&|$)/.test(window.location.search);

const SR_ONLY = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);
  return reduced;
}

function StoreHeroShell() {
  return (
    <div className="store-hero" aria-hidden="true">
      <div className="store-hero-particles" />
      <div className="store-hero-text">
        <h1 className="store-hero-title">{__("STORE")}</h1>
        <span className="store-hero-sub">{__("Spend your Clover Coins")}</span>
      </div>
    </div>
  );
}

function StoreSkeleton() {
  const reduced = usePrefersReducedMotion();
  const shimmer = reduced
    ? { background: "var(--felt-700)" }
    : {
        background:
          "linear-gradient(120deg, var(--felt-700) 30%, var(--gold-a12) 50%, var(--felt-700) 70%)",
        backgroundSize: "200% 100%",
        animation: "storeShimmerX 1.6s infinite",
      };
  const block = (extra) => ({ ...shimmer, borderRadius: 8, ...extra });

  return (
    <div
      className="store-page container-xxl store-page--loading"
      aria-busy="true"
    >
      <span aria-live="polite" style={SR_ONLY}>
        {__("Loading store offers...")}
      </span>
      <StoreHeroShell />
      <div className="store-toolbar" aria-hidden="true">
        <div style={block({ width: 180, height: 40, borderRadius: 12 })} />
        <div style={block({ width: 150, height: 44, borderRadius: 12 })} />
      </div>
      <div className="store-grid" aria-hidden="true">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="store-card"
            style={{ pointerEvents: "none", cursor: "default" }}
          >
            <div style={{ marginBottom: 10 }}>
              <div style={block({ height: 16, width: "60%", marginBottom: 8 })} />
              <div style={block({ height: 11, width: "85%", marginBottom: 6 })} />
              <div style={block({ height: 11, width: "70%" })} />
            </div>
            <div style={block({ height: 150, borderRadius: 12, marginBottom: 10 })} />
            <div style={{ marginTop: "auto" }}>
              <div style={block({ height: 10, width: "40%", marginBottom: 8 })} />
              <div style={block({ height: 6, borderRadius: 4 })} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StoreUnavailable({ onRetry }) {
  return (
    <div className="store-page container-xxl">
      <StoreHeroShell />
      <div className="store-grid">
        <div className="store-empty show" role="alert">
          <div className="store-empty__ico">
            <CloverCrest size={52} />
          </div>
          <div className="store-empty__title">
            {__("THE STORE IS TAKING A BREAK")}
          </div>
          <div className="store-empty__desc">
            {__(
              "We couldn't load the store right now. Please try again in a moment."
            )}
          </div>
          <button
            className="btn-primary"
            type="button"
            onClick={onRetry}
            style={{ marginTop: 16 }}
          >
            {__("Try again")}
          </button>
        </div>
      </div>
    </div>
  );
}

// The authenticated store: this is the ONLY place the personal `/store` fetch
// lives. AuthGate never mounts it for a guest, so the wallet/redeem endpoints
// never fire for a logged-out browser.
function StoreDashboard() {
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [storeData, setStoreData] = useState(null);
  const [isPreviewDemo, setIsPreviewDemo] = useState(false);

  const loadStore = useCallback(async () => {
    setStatus("loading");
    try {
      const response = await api.get("/store");
      if (response.status === 200 && response.data?.data) {
        setStoreData(normalizeStoreData(response.data.data));
        setIsPreviewDemo(false);
        setStatus("ready");
        return;
      }
    } catch (err) {
      // api.get resolves rather than rejects; this only guards local errors.
    }

    if (PREVIEW_ENABLED) {
      setStoreData(getStoreMockState());
      setIsPreviewDemo(true);
      setStatus("ready");
      return;
    }

    setStoreData(null);
    setStatus("error");
  }, []);

  useEffect(() => {
    loadStore();
  }, [loadStore]);

  if (status === "loading") {
    return <StoreSkeleton />;
  }

  if (status === "error" || !storeData) {
    return <StoreUnavailable onRetry={loadStore} />;
  }

  return <StoreContent data={storeData} isPreviewDemo={isPreviewDemo} />;
}

export default function Store(props) {
  // Logged-out visitors get the marketing teaser (working Sign-Up CTA, generic
  // locked sample cards, no balance, no redeem). Only authenticated players
  // reach StoreDashboard and its personal `/store` fetch.
  return (
    <AuthGate teaser={<StoreTeaser {...props} />}>
      <StoreDashboard />
    </AuthGate>
  );
}
